import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, forkJoin, of, pipe, switchMap, tap, throwError } from 'rxjs';
import {
	Option,
	MetaDefinition,
	Contact,
	Campaign,
	Share,
	Account,
	Product,
	Contributor,
	Brand,
	Placement,
	Touchpoint,
} from 'src/app/features/shared/models';
import { NeptuneError } from 'src/app/features/shared/models/Neptune';
import { environment } from 'src/environments/environment';
import { SessionService } from 'src/app/features/shared/services/session.service';
import { BulkRequest, BulkPayload } from 'src/app/features/shared/models/campaigns/store';
import { Permission } from '../../shared/models/HoldingCompany';
import { getStoreKey, StoreId } from 'src/app/store/store.utils';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';

export interface MultiTouchpointBookingsState {
	campaign: Campaign | null;
	placements: Placement[];
	definitions: MetaDefinition[];
	contacts: Contact[];
	accounts: Account[];
	brands: Brand[];
	products: Product[];
	contributors: Contributor[];
	types: Share[];
	touchpoints: Touchpoint[];
	loading: {
		saving: boolean;
		deleting: boolean;
		publishing: boolean;
		confirming: boolean;
		brands: boolean;
		availability: boolean;
	};
	error: {
		campaign: NeptuneError | null;
		availability: NeptuneError | null;
	};
	initialized: boolean;
}

const initialState: MultiTouchpointBookingsState = {
	campaign: {
		id: null,
		name: null,
		start: null,
		end: null,
		products: [],
		budget: null,
		accounts: [],
		contacts: [],
		contributors: [],
		meta: [],
		house: false,
	},
	placements: [],
	accounts: [],
	brands: [],
	contacts: [],
	products: [],
	contributors: [],
	definitions: [],
	types: [],
	touchpoints: [],
	loading: {
		saving: false,
		deleting: false,
		publishing: false,
		confirming: false,
		brands: false,
		availability: false,
	},
	error: {
		campaign: null,
		availability: null,
	},
	initialized: false,
};

const CAMPAIGN_STORE_KEY = StoreId.CAMPAIGN;

export const CampaignStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withDevtools(CAMPAIGN_STORE_KEY),
	withMethods((store, http = inject(HttpClient), session = inject(SessionService)) => ({
		loadCampaign: rxMethod<{ tenantId: number; campaignId?: string }>(
			pipe(
				tap(() => updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Load campaign'), { initialized: false })),
				switchMap(({ tenantId, campaignId }: { tenantId: number; campaignId?: string }) =>
					forkJoin({
						initialize: http.get<any>(
							`${environment.api}/client_portal/campaigns/initialize/?holdingCompanyId=${tenantId}`,
						),
						shares: http.get<Share[]>(`${environment.api}/client_portal/${tenantId}/shares/`),
						accounts: http.get<Account[]>(`${environment.api}/client_portal/${tenantId}/accounts/`),
						products: http.get<Product[]>(`${environment.api}/client_portal/${tenantId}/products/`),
						definitions: http.get<MetaDefinition[]>(`${environment.api}/client_portal/${tenantId}/meta-definitions/`),
						contributors: http.get<Contributor[]>(`${environment.api}/client_portal/${tenantId}/contributors/`),
						campaign: campaignId
							? http.get<Campaign>(`${environment.api}/client_portal/${tenantId}/campaigns/${campaignId}/`)
							: of(null),
						placements: campaignId
							? http.get<any[]>(`${environment.api}/client_portal/${tenantId}/campaigns/${campaignId}/placements/`)
							: of([]),
					}).pipe(
						tap({
							next: ({ initialize, shares, accounts, products, definitions, contributors, campaign, placements }) => {
								const touchpoints = initialize.touchpoints.flatMap((touchpoint: any) =>
									touchpoint.categories.map((category: any) => ({
										id: category.id,
										name: touchpoint.name + ': ' + category.name,
										image: !!category.image ? category.image : touchpoint.image,
										stores: category.stores,
										touchpoint: {
											id: touchpoint.id,
											name: touchpoint.name,
										},
									})),
								);

								updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Load campaign', true), {
									touchpoints: touchpoints,
									types: shares.filter((item: any) => {
										if (!session.permissions().includes(Permission.CAMPAIGN_IN_HOUSE) && item.portion === 0) {
											return false;
										}
										return true;
									}),
									campaign: campaign,
									accounts: accounts,
									products: products,
									contributors: contributors,
									definitions: definitions,
									placements: placements,
									initialized: true,
								});
							},
							error: (error: NeptuneError) =>
								updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Load campaign', false), {
									error: {
										...store.error(),
										campaign: error,
									},
								}),
						}),
					),
				),
			),
		),
		loadBrands: rxMethod<{ tenantId: number; accountId: number }>(
			pipe(
				tap(() =>
					updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Load brands'), {
						loading: { ...store.loading(), brands: true },
					}),
				),
				switchMap(({ tenantId, accountId }: { tenantId: number; accountId: number }) => {
					updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Load brands', true), {
						loading: { ...store.loading(), brands: true },
					});
					return http.get<Brand[]>(`${environment.api}/client_portal/${tenantId}/accounts/${accountId}/brands/`).pipe(
						tap({
							next: (brands) => {
								updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Load brands', true), {
									brands: brands,
									loading: { ...store.loading(), brands: false },
								});
							},
						}),
					);
				}),
			),
		),
		resetBrands: rxMethod<void>(
			pipe(tap(() => updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Reset brands'), { brands: [] }))),
		),

		saveCampaign: (request: BulkPayload, confirm: boolean = false) => {
			const updatedLoading = confirm ? { confirming: true } : { saving: true };

			updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Save campaign'), {
				loading: {
					...store.loading(),
					...updatedLoading,
				},
			});

			const { tenant, campaign, placements, account, brands, products, contacts } = request;

			const payload: BulkRequest = {
				campaign: campaign,
				placements: placements,
			};

			if (account) {
				payload.account = account;
			}
			if (brands && brands.length > 0) {
				payload.brands = brands;
			}
			if (products && products.length > 0) {
				payload.products = products;
			}

			if (contacts && contacts.length > 0) {
				payload.contacts = contacts;
			}

			if (confirm) {
				payload.confirm = true;
			}

			return http
				.post<{
					campaign: Campaign;
					placements: Placement[];
				}>(`${environment.api}/client_portal/internal/${tenant}/campaigns/bulk/`, payload)
				.pipe(
					tap(() => {
						updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Save campaign', true), {
							loading: {
								...store.loading(),
								saving: false,
								confirming: false,
							},
						});
						if (confirm) {
							window.location.reload();
						}
					}),
					catchError((error) => {
						updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Save campaign', true), {
							loading: {
								...store.loading(),
								saving: false,
								confirming: false,
							},
							error: error,
						});
						return throwError(() => error);
					}),
				);
		},

		deleteCampaign: ({ tenantId, campaignId }: { tenantId: number; campaignId: string }) => {
			updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Delete campaign'), {
				loading: { ...store.loading(), deleting: true },
			});
			return http.delete(`${environment.api}/client_portal/${tenantId}/campaigns/${campaignId}/`).pipe(
				tap(() => {
					updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Delete campaign', true), {
						loading: { ...store.loading(), deleting: false },
					});
				}),
			);
		},

		pauseCampaign: ({ tenantId, campaignId }: { tenantId: number; campaignId: string }) => {
			updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Pause campaign', true), {
				loading: { ...store.loading(), publishing: true },
			});
			return http
				.patch<Campaign>(`${environment.api}/client_portal/${tenantId}/campaigns/${campaignId}/pause/`, {})
				.pipe(
					tap((response) => {
						updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Pause campaign', true), {
							campaign: { ...store.campaign()!, status: response.status },
							loading: { ...store.loading(), publishing: false },
						});
					}),
				);
		},

		updateContributors: (contributors: Option[], tenantId: number, campaignId: string) => {
			const active = store.contributors().filter((one) => contributors.some((two) => two.id === one.id));
			updateState(store, getStoreKey(CAMPAIGN_STORE_KEY, 'Update contributors'), {
				campaign: { ...store.campaign()!, contributors: active },
			});
			return http.patch<Campaign>(`${environment.api}/client_portal/${tenantId}/campaigns/${campaignId}/`, {
				contributors: active.map((contributor) => contributor.id),
			});
		},
	})),
);
